/**
 * Every user-facing string on /about/stack (the tech stack page, the "further detail"
 * behind the AI Spine page's Under the hood section), as data. Ported from
 * docs/tech-stack-overview.html: same content, front-door styling.
 *
 * vocabulary.test.ts checks the headline copy here (titles, eyebrows, the lede). The
 * builder-facing detail (diagram labels, layer chips and blurbs, step bodies, package
 * names) is exempt, because naming Claude, gp-sdk and Module Federation is its job.
 * stackContent.test.ts keeps PINNED_VERSIONS equal to the workspace overrides.
 *
 * Text in `backticks` renders as inline code (see withCode in AboutStack.tsx).
 */
import type { StackIconId } from './stackIcons';

/** A chip's mark: a Simple Icons brand mark, or the green ring used where none exists. */
export type ChipIcon = StackIconId | 'ring';

export interface StackChip {
  icon: ChipIcon;
  name: string;
  detail: string;
}

export interface StackLayer {
  id: 'shell' | 'api' | 'ai' | 'data' | 'identity' | 'delivery';
  title: string;
  blurb: string;
  chips: readonly StackChip[];
}

export interface JoinStep {
  title: string;
  body: string;
}

export interface PinnedVersion {
  /** The workspace override key(s) this row reports, e.g. ['react', 'react-dom']. */
  packages: readonly string[];
  version: string;
}

/** The browser tab title for /about/stack. */
export const STACK_DOCUMENT_TITLE = 'Tech stack — The AI Spine — Growth Platforms App Store';

export const STACK_HERO = {
  back: '← Back to the AI Spine',
  eyebrow: 'Growth Platforms App Store · Tech stack',
  titleLead: 'The AI',
  titleEmphasis: 'Spine',
  titleTail: ', layer by layer',
  lede:
    'One shared shell hosts many apps, each built and deployed by its own team. The shell handles sign-in, navigation, access control, data and AI for all of them, and the whole thing runs serverless on AWS.',
} as const;

export const STACK_FACTS: readonly string[] = [
  'Serverless only: no servers, VPC or Docker',
  'One API on Lambda serves every app',
  'Apps load at runtime and ship on their own',
  'Apps never hold an AI key',
];

export const ARCHITECTURE_SECTION = {
  title: 'How the pieces connect',
  sub: "A user signs in once through firm SSO. The browser loads the shell and each app's bundle from CloudFront, then calls one API for everything else.",
  diagramLabel: 'Architecture diagram',
  scrollHint: 'Scroll sideways to see the whole diagram.',
} as const;

/** One box in the architecture diagram, in viewBox units (1000 × 420). */
export interface DiagramNode {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** A brand mark, or 'dot' for a green dot where none exists. */
  mark: StackIconId | 'dot';
  title: string;
  subs: readonly string[];
  /** Outlined in green: the AI service. */
  highlight?: boolean | undefined;
}

/* Exempt from the vocabulary guard: the diagram names Claude and the services by design.
   Edge geometry lives in StackDiagram.tsx; the words and boxes live here. */
export const ARCHITECTURE_DIAGRAM = {
  ariaLabel:
    'Architecture: users load the shell from CloudFront and S3, sign in through Cognito and Entra ID, and call the platform API on Lambda through API Gateway. The API reaches DynamoDB, Secrets Manager, Claude, AirTable, Microsoft Graph and SES. App teams deploy through GitHub Actions to their own S3 prefix.',
  lanes: [
    { x: 20, label: 'People' },
    { x: 220, label: 'Edge & delivery' },
    { x: 470, label: 'Compute' },
    { x: 760, label: 'Data & services' },
  ],
  edgeLabels: {
    apiCalls: 'API calls with sign-in token',
    signIn: 'sign in',
    writesOnly: 'writes only',
    appPrefix: '/apps/<id>/',
    aiStreams: 'AI streams',
  },
} as const;

/* Exempt from the vocabulary guard, for the same reason as ARCHITECTURE_DIAGRAM. */
export const DIAGRAM_NODES: readonly DiagramNode[] = [
  { id: 'users', x: 20, y: 40, w: 160, h: 70, mark: 'dot', title: 'Deloitte users', subs: ['any modern browser'] },
  { id: 'cognito', x: 20, y: 150, w: 160, h: 70, mark: 'amazoncognito', title: 'Cognito', subs: ['federated to Entra ID', 'with MFA'] },
  { id: 'teams', x: 20, y: 320, w: 160, h: 70, mark: 'dot', title: 'App teams', subs: ['own repo per app'] },
  { id: 'cloudfront', x: 220, y: 40, w: 200, h: 60, mark: 'dot', title: 'CloudFront + WAF (prod)', subs: ['serves shell and app bundles'] },
  { id: 's3', x: 220, y: 130, w: 200, h: 60, mark: 'amazons3', title: 'S3 web bucket', subs: ['shell at /, apps at /apps/<id>/'] },
  { id: 'actions', x: 220, y: 320, w: 200, h: 70, mark: 'githubactions', title: 'GitHub Actions', subs: ['OIDC, no stored AWS keys', 'scoped deploy role per app'] },
  { id: 'apigw', x: 470, y: 40, w: 220, h: 60, mark: 'amazonapigateway', title: 'API Gateway (HTTP API)', subs: ['checks the sign-in token first'] },
  { id: 'api', x: 470, y: 130, w: 220, h: 80, mark: 'hono', title: 'Platform API', subs: ['Hono on Lambda, Node 22, Graviton', 'registry · RBAC · AI · data tools'] },
  { id: 'stream', x: 470, y: 240, w: 220, h: 60, mark: 'awslambda', title: 'AI streaming Lambda', subs: ['IAM-auth URL behind CloudFront'] },
  { id: 'nightly', x: 470, y: 330, w: 220, h: 60, mark: 'amazoncloudwatch', title: 'Nightly jobs', subs: ['EventBridge: roster sync, usage rollup'] },
  { id: 'dynamodb', x: 760, y: 40, w: 220, h: 50, mark: 'amazondynamodb', title: 'DynamoDB', subs: ['app registry, RBAC, audit log, usage'] },
  { id: 'secrets', x: 760, y: 102, w: 220, h: 50, mark: 'awssecretsmanager', title: 'Secrets Manager', subs: ['Anthropic and AirTable keys'] },
  { id: 'claude', x: 760, y: 164, w: 220, h: 50, mark: 'claude', title: 'Claude (Anthropic API)', subs: ['model policy, per-user limits'], highlight: true },
  { id: 'airtable', x: 760, y: 226, w: 220, h: 50, mark: 'airtable', title: 'AirTable', subs: ['read through tools, fields suppressed'] },
  { id: 'graph', x: 760, y: 288, w: 220, h: 50, mark: 'dot', title: 'Microsoft Graph', subs: ['firm-wide people search'] },
  { id: 'ses', x: 760, y: 350, w: 220, h: 50, mark: 'amazonsimpleemailservice', title: 'SES via SQS', subs: ['paced outbound email'] },
];

export const LAYERS_SECTION = {
  title: 'What we use, and where',
  sub: 'Six layers. Everything is TypeScript, kept in one pnpm monorepo, and deployed as code.',
} as const;

/* Exempt from the vocabulary guard (titles excepted): the blurbs and chips are the
   builder-facing detail, where Claude, gp-sdk and Module Federation are named by design. */
export const STACK_LAYERS: readonly StackLayer[] = [
  {
    id: 'shell',
    title: 'Shell & apps',
    blurb: 'A React shell loads each app at runtime. Apps are content-only screens that get login, layout and navigation for free.',
    chips: [
      { icon: 'react', name: 'React 19', detail: 'shell and every app' },
      { icon: 'vite', name: 'Vite 8', detail: 'build and local dev' },
      { icon: 'ring', name: 'Module Federation', detail: 'apps plug in as runtime remotes' },
      { icon: 'ring', name: 'Cloudscape', detail: 'the only UI kit, Deloitte-themed with Open Sans, in light or dark mode' },
      { icon: 'reactrouter', name: 'React Router · TanStack Query · Zustand', detail: 'routing, server data, shared state' },
    ],
  },
  {
    id: 'api',
    title: 'Platform API',
    blurb: 'One Hono app on Lambda serves the app registry, access control, the AI endpoints and data tools.',
    chips: [
      { icon: 'nodedotjs', name: 'Node.js 22 on Graviton', detail: 'arm64 Lambda, bundled with esbuild' },
      { icon: 'hono', name: 'Hono', detail: 'one router for every route' },
      { icon: 'zod', name: 'Zod', detail: 'validates every request at the boundary' },
      { icon: 'amazonapigateway', name: 'API Gateway HTTP API', detail: 'Cognito JWT authorizer' },
      { icon: 'awslambda', name: 'Middy + Powertools', detail: 'structured logs, metrics, X-Ray tracing' },
    ],
  },
  {
    id: 'ai',
    title: 'AI',
    blurb: 'Apps call the AI through gp-sdk and never see a key. The platform sets which models apps may use, limits each user, and turns AI on per app.',
    chips: [
      { icon: 'claude', name: 'Claude', detail: 'the only LLM provider' },
      { icon: 'anthropic', name: 'Anthropic SDK', detail: 'buffered `/api/ai/chat` and streaming `/api/ai/stream`' },
      { icon: 'awssecretsmanager', name: 'Secrets Manager', detail: 'holds the one Anthropic key' },
      { icon: 'ring', name: 'Controls', detail: 'model policy, per-user limits, per-app `aiEnabled`, usage by app' },
      { icon: 'ring', name: 'App tools', detail: 'each app can hand the assistant its own tools and instructions (`toolsUrl`)' },
    ],
  },
  {
    id: 'data',
    title: 'Data & integrations',
    blurb: 'Managed stores only. Outside data comes through platform-owned tools, never raw credentials.',
    chips: [
      { icon: 'amazondynamodb', name: 'DynamoDB', detail: 'registry, RBAC data, audit trail, App Store and AI usage' },
      { icon: 'amazons3', name: 'S3', detail: 'web bundles and precomputed results' },
      { icon: 'airtable', name: 'AirTable', detail: 'roster and app data, sensitive fields stripped' },
      { icon: 'ring', name: 'Microsoft Graph', detail: 'people search across the firm' },
      { icon: 'amazonsimpleemailservice', name: 'SES + SQS', detail: 'paced outbound email from a Deloitte address' },
      { icon: 'amazonrds', name: 'Aurora Serverless v2', detail: 'optional per environment, off by default' },
    ],
  },
  {
    id: 'identity',
    title: 'Identity & security',
    blurb: 'Firm sign-in at the edge, and an app only loads when it is enabled.',
    chips: [
      { icon: 'amazoncognito', name: 'Cognito + Entra ID', detail: 'firm SSO with MFA, PKCE in the browser' },
      { icon: 'ring', name: 'RBAC decision point', detail: 'roles, teams and alliance access' },
      { icon: 'ring', name: 'WAF (prod only)', detail: 'AWS managed rules in front of CloudFront in production' },
      { icon: 'amazonwebservices', name: 'Scoped deploy roles', detail: 'each app can write only its own prefix' },
      { icon: 'snyk', name: 'Snyk', detail: 'dependency scans in CI' },
    ],
  },
  {
    id: 'delivery',
    title: 'Build & delivery',
    blurb: 'Infrastructure is code. Changes ship through GitHub Actions with a reviewer gate for prod.',
    chips: [
      { icon: 'amazonwebservices', name: 'AWS SAM / CloudFormation', detail: 'shared, api, web and role stacks' },
      { icon: 'githubactions', name: 'GitHub Actions + OIDC', detail: 'path-routed deploys for web, API, infra' },
      { icon: 'github', name: 'GitHub Packages', detail: 'publishes `gp-sdk` to app builders' },
      { icon: 'pnpm', name: 'pnpm · TypeScript 6', detail: 'one monorepo: web, api, sdk, infra' },
      { icon: 'vitest', name: 'Vitest + ESLint', detail: 'lint, typecheck and test on every PR' },
      { icon: 'amazoncloudwatch', name: 'CloudWatch', detail: 'logs, alarms, email reputation alerts' },
    ],
  },
];

export const JOIN_SECTION = {
  title: 'How an app joins',
  sub: 'No AWS access is needed to build and preview an app locally.',
} as const;

/* Exempt from the vocabulary guard (titles excepted): the bodies name gp-sdk and Claude Code. */
export const JOIN_STEPS: readonly JoinStep[] = [
  {
    title: 'Start from the template',
    body: 'New repos come from `gp-app-template`, which ships the build-gp-app skill for Claude Code.',
  },
  {
    title: 'Build a screen',
    body: 'A React component that uses `@deloitte-us-consulting/gp-sdk` for auth, layout, notifications, AI and data.',
  },
  {
    title: 'Deploy to its own prefix',
    body: "The app's CI assumes a scoped role that can only write to `/apps/<id>/`.",
  },
  {
    title: 'Turn it on',
    body: 'An admin enables the app in the registry, sets its stage (Demo, In dev, Beta or Live), and turns AI on separately. The shell then loads it for users with access.',
  },
];

export const VERSIONS_SECTION = {
  title: 'Pinned versions',
  sub: 'These libraries are shared between the shell and every app, so all of them must match exactly.',
  packageHeader: 'Package',
  versionHeader: 'Version',
  note: 'Locked in `pnpm-workspace.yaml` and `docs/contract.md`, and declared as shared singletons in every `vite.config`. Bump them together or not at all.',
} as const;

/* Exempt from the vocabulary guard: package names are data, not copy. Kept equal to the
   `overrides:` block in pnpm-workspace.yaml (and docs/contract.md) by stackContent.test.ts. */
export const PINNED_VERSIONS: readonly PinnedVersion[] = [
  { packages: ['react', 'react-dom'], version: '19.2.8' },
  { packages: ['@cloudscape-design/components'], version: '3.0.1357' },
  { packages: ['@cloudscape-design/global-styles'], version: '1.0.67' },
  { packages: ['react-router'], version: '8.3.1' },
  { packages: ['@tanstack/react-query'], version: '5.102.8' },
  { packages: ['zustand'], version: '5.0.15' },
];

export const STACK_FOOTER = {
  source:
    'Drawn from the gp-platform repo: the architecture doc, the shared-version contract and the SAM templates. Logos from Simple Icons; trademarks belong to their owners.',
  back: '← Back to the AI Spine',
} as const;
